import axios from 'axios'
import {NOTIFICATIONS_PAGE_GAP, RIGHTSHOLDERS_PAGE_GAP, STATEMENTS_PAGE_GAP} from "@/plugins/store/constants";
import loGet from 'lodash/get'
import loIsEmpty from 'lodash/isEmpty'
import loUpperFirst from 'lodash/upperFirst'
import Vue from 'vue';
import store from './store'
import i18n from '@/plugins/i18n'

const getParamsString = (params) => {
  const urlsp = new URLSearchParams();
  for (let key in params) {
    // eslint-disable-next-line no-prototype-builtins
    if (params.hasOwnProperty(key)) {
      // eslint-disable-next-line no-undefined
      if (![null, undefined].includes(params[key])) {
        urlsp.append(key, params[key]);
      }
    }
  }
  return '?' + urlsp.toString();
}

const API = {
  apiClient: null,
  baseUrl: process.env.VUE_APP_BACKEND,
  endpointList: {},
  isReady: false,
  hangingHandlers: [],

  ready(f) {
    if (this.isReady) {
      return f();
    }

    this.hangingHandlers.push(f);

    return new Promise((resolve) => {
      const int = setInterval(() => {
        if (this.isReady) {
          clearInterval(int);
          resolve(f());
        }
      }, 40)
    })
  },

  execReady() {
    console.log('[api] execReady() – API is now ready')
    this.isReady = true;
    // this.hangingHandlers.forEach((f) => f());
  },

  init(token) {
    return new Promise((resolve, reject) => {
      //- Axios instance
      this.apiClient = axios.create({
        baseURL: this.baseUrl,
        withCredentials: false,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      })

      //- Timeout
      this.apiClient.defaults.timeout = 120000

      // Handle errors
      const errorHandler = async error => {
        if (error.response && error.response.status) {
          let locale, msg
          try {
            locale = loUpperFirst(store.getters['app/locale'])
          } catch (e) {
            locale = 'Sv'
          }
          switch (error.response.status) {
            case 401: 
              //- Stop notifications
              store.dispatch('app/stopNotificationsPoll')

              await Vue.prototype.$dialog.confirm({
                text:  i18n.t('app.loggedout'),
                title: i18n.t('app.loggedout-title'),
                actions: {
                  true: i18n.t('app.ok')
                }
              })

              console.log('401 Error: ', error)

              //- Logout user
              return Vue.prototype.$auth.logout({
                returnTo: window.location.origin,
              });
                  
              //break;
            case 403: 
              msg = loGet(error.response, 'data', null);
              if (msg && msg.errorMsg) {
                Vue.prototype.$dialog.error({
                  // eslint-disable-next-line dot-notation
                  text: msg.errorMsg[locale],
                  title: msg.errorID,
                  icon: false
                })
              }
              console.log('403 Error: ', error)
              break;
            case 502:
              await Vue.prototype.$dialog.error({
                text: "Ifpi's web portal for rightholders is currently unavailable. Please close this window and try again later.",
                title: 'Service unavailable',
                icon: false
              })
              break;
            case 503:
              //- Stop notifications
              store.dispatch('app/stopNotificationsPoll')

              msg = loGet(error.response, 'data', null);
              if (msg && msg.errorMsg) {
                await Vue.prototype.$dialog.error({
                  // eslint-disable-next-line dot-notation
                  text: msg.errorMsg[locale],
                  title: msg.errorID,
                  icon: false
                })
              }

              console.log('503 Error: ', error)

              //- Logout user
              return Vue.prototype.$auth.logout({
                returnTo: window.location.origin,
              });

              //break;
            default:
              console.log('Error: ', error)
              Vue.prototype.$toast(i18n.t('app.requestError'))
          }
        }
        return Promise.reject(error)
      }

      // Handle responses
      const successHandler = response => {
        return response
      }

      // Set interceptors
      this.apiClient.interceptors.response.use(
        response => successHandler(response),
        error => errorHandler(error)
      )

      //- Return client or Error
      if (this.apiClient) {

        this.loadConfig()
          .then(() => {
            this.apiClient.isInitialized = true
            API.execReady();
            resolve(this.apiClient)
          })

      } else {
        reject(new Error('Failed to initialize API'))
      }
    })
  },

  //- Housekeeping
  setHeaders(key, val) {
    this.apiClient.defaults.headers.common[key] = val
  },
  getBaseUrl() {
    return this.baseUrl
  },

  loadConfig() {
    console.log('[api] loadConfig() start')
    return this.apiClient.get(this.baseUrl + '/api/v1/clientconfig')
      // eslint-disable-next-line arrow-parens
      .then((result) => {
        const rows = result.data.rows;
        this.endpointList = rows.reduce((acc, curr) => ({...acc, [curr.name]: curr}), {});
        console.log('[api] loadConfig() done –', Object.keys(this.endpointList).length, 'endpoints')
      });
  },

  //- Backend requests
  settings() {
    return this.apiClient.get(this.baseUrl + '/api/v1')
  },

  rights() {
    return this.apiClient.get(this.baseUrl + '/api/v1/rights')
  },

  profile() {
    return this.apiClient.get(this.baseUrl + '/api/v1/profile')
  },

  errorPage(code) {
    return this.apiClient.get(this.baseUrl + '/api/v1/testerror/' + code)
  },

  anywhere(method, go, body, bol) {
    let params
    try {
      params = ['patch', 'post'].includes(method.toLowerCase()) && body.length > 0 ? JSON.parse(body) : body
    } catch {
      // eslint-disable-next-line no-alert
      alert('invalid JSON in body')
      return
    }
    const url = this.baseUrl + go + getParamsString({bol})
    return this.apiClient[method.toLowerCase()](url, params);
  },

  actions: {

    Content: {
      getHomePage(val) {
        const lang = val === 'en' ? 'en' : 'sv';
        const ep = API.endpointList['start-loggedin'].path + getParamsString({lang});
        return API.apiClient.get(ep);
      },
      getStartPage(val) {
        const lang = val === 'en' ? 'en' : 'sv';
        const ep = API.endpointList['start-loggedout'].path + getParamsString({lang});
        return API.apiClient.get(ep);
      },
      getLoginPage(val) {
        const ep = API.endpointList['login-page'].path + getParamsString({lang: val});
        return API.apiClient.get(ep);
      },
      getDocumentPage(val) {
        const ep = API.endpointList['document-page'].path + getParamsString({lang: val});
        return API.apiClient.get(ep);
      }
    },

    Claimforms: {
      getList(bol) {
        const ep = API.endpointList['claimforms-list'].path + getParamsString({bol});
        return API.apiClient.get(ep);
      },

      create(bol, data) {
        const ep = API.endpointList['claimforms-list'].path;
        return API.apiClient.post(ep, {
          ...data,
          bol
        });
      },

      getDetails(bol, clnr) {
        const ep = API.endpointList['claimforms-details'].path.replace(':clnr', clnr) + getParamsString({bol});
        return API.apiClient.get(ep);
      },

      getDetailsBlob(bol, clnr, params) {
        console.log(bol,clnr,params)
        const ep = API.endpointList['claimforms-details-rows'].path.replace(':clnr', clnr) + getParamsString({bol, ...params});
        return API.apiClient.get(ep, {
          responseType: 'blob',
        });
      },

      addRows(bol, clnr, data) {
        const ep = API.endpointList['claimforms-details'].path.replace(':clnr', clnr)
        return API.apiClient.post(ep, {
          bol,
          ...data,
        });
      },

      cancel(bol, {clnr, remove = 0}) {
        const ep = API.endpointList['claimforms-details'].path.replace(':clnr', clnr) + getParamsString({bol, delete: remove ? 1 : null});
        return API.apiClient.delete(ep);
      },

      getRows(bol, clnr) {
        const ep = API.endpointList['claimforms-details-rows'].path.replace(':clnr', clnr) + getParamsString({
          bol,
          limit: 10000
        });
        return API.apiClient.get(ep);
      },

      updateRows(bol, clnr, data) {
        const ep = API.endpointList['claimforms-details-rows'].path.replace(':clnr', clnr) + getParamsString({bol});
        return API.apiClient.post(ep, data);
      },

      getRowsDetails(bol, clnr, clrnr) {
        const ep = API.endpointList['claimforms-details-rows'].path.replace(':clnr', clnr)
          .replace(':clrnr', clrnr) + getParamsString({bol});
        return API.apiClient.get(ep);
      },

      deleteRow(bol, clnr, clrnr) {
        const ep = API.endpointList['claimforms-details-rows-details'].path.replace(':clnr', clnr)
          .replace(':clrnr', clrnr) + getParamsString({bol});
        return API.apiClient.delete(ep);
      },

      updateStatus(bol, {clnr, status, ...params}) {
        const ep = API.endpointList['claimforms-status'].path.replace(':clnr', clnr) + getParamsString({bol});
        return API.apiClient.patch(ep, {...params, status});
      },
    },
    Documents: {
      Public: {
        getList() {
          const ep = API.endpointList['documents-public-list'].path;
          return API.apiClient.get(ep)
        },

        getDetails(id) {
          const ep = API.endpointList['document-public-detail'].path.replace(':docid', id);
          return API.apiClient.get(ep);
        },

        getFile(uri) {
          const ep = API.endpointList['document-public-file'].path.replace(':docuri', window.encodeURIComponent(uri));
          return API.apiClient.get(ep, {
            responseType: 'blob'
          });
        },

        getFileUri(uri) {
          return API.endpointList['document-public-file'].path.replace(':docuri', uri);
        }
      },

      Private: {

        getXLSXTemplate(bol) {
          return API.anywhere('get', '/api/v1/private/claim-example' + getParamsString({bol}));
        },

        getList() {
          const ep = API.endpointList['documents-list'].path;
          return API.apiClient.get(ep)
        },

        getDetails(id) {
          const ep = API.endpointList['document-detail'].path.replace(':docid', id);
          return API.apiClient.get(ep);
        },

        getFile(uri) {
          const ep = API.endpointList['document-file'].path.replace(':docuri', window.encodeURIComponent(uri));
          return API.apiClient.get(ep, {
            responseType: 'blob'
          });
        }
      }
    },
    Messages: {
      getList(bol, page = 1, limit = NOTIFICATIONS_PAGE_GAP) {
        const ep = API.endpointList['messages-list'].path + getParamsString({bol, page, limit});
        return API.apiClient.get(ep);
      },

      // ?? What does this thing do?
      updateList() {
        const ep = API.endpointList['messages-list'].path;
        return API.apiClient.post(ep);
      },

      getSentList() {
        const ep = API.endpointList['messages-sent-list'].path;
        return API.apiClient.get(ep);
      },

      // MB sets message as read ?
      markRead(mid, bol) {
        const ep = API.endpointList['messages-details'].path.replace(':mid', mid) + getParamsString();
        return API.apiClient.patch(ep, {bol, "lasstatus": 1});
      },

      sendMessage({rows, title, text, tbol = null, bol = null}) {
        const ep = API.endpointList['messages-list'].path;
        return API.apiClient.post(ep, {
          rows,
          head: title,
          message: text,
          ...(tbol === null ? {} : {tbol}),
          ...(bol === null ? {} : {bol}),
        });
      },

      sendReply(mid, {title, text, bol = null}) {
        const ep = API.endpointList['message-reply'].path.replace(':mid', mid);

        return API.apiClient.post(ep, {
          head: title,
          message: text,
          bol
        });
      },

      getMessageDetails(mid) {
        const ep = API.endpointList['messages-details'].path.replace(':mid', mid);
        return API.apiClient.get(ep);
      },

      getMessageRepliesList(mid) {
        const ep = API.endpointList['messages-details-replies-list'].path.replace(':mid', mid);
        return API.apiClient.get(ep);
      },
    },
    Rightsholders: {
      getList(page = 1, limit = RIGHTSHOLDERS_PAGE_GAP) {
        const ep = API.endpointList['rightholders-list'].path + getParamsString({page, limit});
        return API.apiClient.get(ep);
      },
      getDetails(bol) {
        const ep = API.endpointList['rightholders-details'].path.replace(':bol', bol);
        return API.apiClient.get(ep);
      }
    },
    Rights: {
      getList(bol, page = null, params) {
        const ep = API.endpointList['rights-list'].path + getParamsString({bol, page, ...params});
        return API.apiClient.get(ep);
      },

      getListBlob(bol, params) {
        //- Remove empty search
        if (params && params.search !== 'undefined' && loIsEmpty(params.search)) {
          delete params.search
        }
        const ep = API.endpointList['rights-list'].path + getParamsString({bol, ...params});
        return API.apiClient.get(ep, {
          responseType: 'blob'
        });
      },

      getDetails(isrc, bol) {
        const ep = API.endpointList['rights-details'].path.replace(':isrc', isrc) + getParamsString({bol});
        return API.apiClient.get(ep);
      },
    },
    Settings: {
      fetch() {
        const ep = API.endpointList.settings.path;
        return API.apiClient.get(ep);
      },

      updateRightsholder(bol) {
        const ep = API.endpointList['settings-setbol'].path.replace(':bol', bol);
        return API.apiClient.get(ep);
      },

      getRightsholderForm(bol, docuri) {
        const ep = API.endpointList['user-form'].path.replace(':docuri', docuri) + getParamsString({bol});
        return API.apiClient.get(ep);
      },

      updateLang(lang) {
        const ep = API.endpointList['settings-setlang'].path.replace(':lang', lang);
        return API.apiClient.get(ep);
      }
    },
    Statements: {
      getList(bol, page = 1, limit = STATEMENTS_PAGE_GAP) {
        const ep = API.endpointList['statements-list'].path + getParamsString({bol, page, limit});
        return API.apiClient.get(ep);
      },

      getStatementDetails(id) {
        const ep = API.endpointList['statements-details'].path.replace(':sfnr', id);
        return API.apiClient.get(ep);
      },

      getDetailsBlob(id, bol) {
        const ep = API.endpointList['statements-details-letter'].path.replace(':sfnr', id) + getParamsString({bol});
        return API.apiClient.get(ep, {
          responseType: 'blob',
        });
      },

      getPlaylistBlob(id, bol) {
        const ep = API.endpointList['statements-details-playlist'].path.replace(':sfnr', id) + getParamsString({bol});
        return API.apiClient.get(ep, {
          responseType: 'blob',
        });
      }
    },
    Unclaimed: {
      getList(bol, page, params) {
        const ep = API.endpointList['unclaimed-list'].path + getParamsString({bol, page, ...params});
        return API.apiClient.get(ep)
      },

      getListBlob(bol, params) {
        //- Remove empty search
        if (params && params.search !== 'undefined' && loIsEmpty(params.search)) {
          delete params.search
        }
        const ep = API.endpointList['unclaimed-list'].path + getParamsString({bol, ...params});
        return API.apiClient.get(ep, {
          responseType: 'blob'
        });
      },
    },
    Staging: {
      getISRCForSpotifyLink(uri) {
        const ep = API.endpointList['spotify-isrc'].path.replace(':uri', encodeURIComponent(uri));
        return API.apiClient.get(ep);
      },

      getFiles(bol, page, params) {
        const ep = API.endpointList['staging-files-list'].path + getParamsString({bol, page, ...params});
        return API.apiClient.get(ep);
      },

      getFile(bol, nb1id, page, params) {
        const ep = API.endpointList['staging-files-details-rows'].path.replace(':nb1id', encodeURIComponent(nb1id)) + getParamsString({bol, page, ...params});
        return API.apiClient.get(ep);
      },

      getFileBlob(bol, nb1id, params) {
        const ep = API.endpointList['staging-files-details-rows'].path.replace(':nb1id', encodeURIComponent(nb1id)) + getParamsString({bol, ...params});
        return API.apiClient.get(ep,{
          responseType: 'blob'
        });
      },

      getList(bol, page, params) {
        const ep = API.endpointList['staging-list'].path + getParamsString({bol, page, ...params});
        return API.apiClient.get(ep);
      },

      getListBlob(bol, params) {
        if (params && params.search !== 'undefined' && loIsEmpty(params.search)) {
          delete params.search
        }
        const ep = API.endpointList['staging-list'].path + getParamsString({bol, ...params});
        return API.apiClient.get(ep, {
          responseType: 'blob'
        });
      },

      add(bol, params) {
        const ep = API.endpointList['staging-add'].path + getParamsString({bol})
        return API.apiClient.post(ep, params);
      },

      uploadFile(bol, files, revoke = false, commit = false) {
        const promises = []
        files.forEach((file) => {
          const ep = API.endpointList['staging-add-file'].path + getParamsString({bol});
          const fd = new FormData();
          fd.append('file', file);
          if (revoke) {
            fd.append('revoke', true)
          }
          if (commit) {
            fd.append('commit', true)
          }
          promises.push(API.apiClient.post(ep, fd))
        })
        return Promise.all(promises)
      },

      getRow({ityp, isrc, ...params}) {
        const ep = API.endpointList['staging-row'].path.replace(':isrc', isrc)
          .replace(':ityp', ityp) + getParamsString(params);
        return API.apiClient.get(ep);
      },

      deleteRow({ityp, isrc, bol}) {
        const ep = API.endpointList['staging-row'].path.replace(':isrc', isrc)
          .replace(':ityp', ityp) + getParamsString({bol});
        return API.apiClient.delete(ep);
      },

      deleteRows({bol, ...params}) {
        const ep = API.endpointList['staging-rows-delete'].path + getParamsString({bol});
        return API.apiClient.put(ep, {bol, ...params});
      },

      updateRow({bol, ...params}) {
        const ep = API.endpointList['staging-update'].path + getParamsString({bol});
        return API.apiClient.patch(ep, {bol, ...params});
      }
    },
    User: {
      getList() {
        const ep = API.endpointList['user-list'].path;
        return API.apiClient.get(ep);
      },

      getCurrentUserInfo() {
        const ep = API.endpointList['user-profile'].path;
        return API.apiClient.get(ep);
      }
    }
  }
}

export default API
